import type { ReactElement } from 'react';
import cx from 'clsx';
import { useField } from '@ff-it/form';
import { Box, Switch } from '@ff-it/ui';
import type { ProductFormConfig } from '../types';
import type { ConfigProps } from './types';

/**
 * Renders the active product configuration form. The resolved `ProductFormConfig`
 * is supplied by the caller — the route composes it from the type modules, so
 * this component stays free of type-module imports.
 */
export function Config({ config, ...props }: ConfigProps & { config?: ProductFormConfig }): ReactElement | null {
  const {
    input: { value },
  } = useField('configuration', { allowNull: true, subscription: { value: true } });

  const Component = config?.Component;

  return Component && value !== null && value !== undefined ? (
    <fieldset className="mb-2">
      <legend>Configuration</legend>
      <Component {...props} />
    </fieldset>
  ) : null;
}

export function ConfigToggle({
  config,
  className,
  disabled = false,
  beforeDisable,
  id = 'product-toggle',
  help,
}: {
  config?: ProductFormConfig;
  className?: string;
  disabled?: boolean;
  /** Return false to keep the configuration enabled. */
  beforeDisable?: () => boolean | Promise<boolean>;
  id?: string;
  help?: string;
}): ReactElement | null {
  const {
    input: { value, onChange },
  } = useField('configuration', { allowNull: true, subscription: { value: true } });
  if (!config) {
    return null;
  }

  const enabled = value !== null && value !== undefined;
  const onToggle = async () => {
    if (!enabled) {
      onChange(config.initialValues);
      return;
    }
    if (beforeDisable && !(await beforeDisable())) {
      return;
    }
    onChange(null);
  };

  return (
    <div className={cx('form-group', className)}>
      <Box display="flex" alignItems="center" gap="2" mt="1">
        <Switch checked={enabled} disabled={disabled} onChange={onToggle} id={id} />
        <label className="form-check-label" htmlFor={id}>
          Product details
        </label>
      </Box>
      <small className="form-text text-muted">{help ?? config.help ?? 'Add type-specific product details'}</small>
    </div>
  );
}
